// Client-side bookkeeping for the batched bulk hike delete.
//
// `destroyUserHikes` is resumable: it works inside a server-side time budget and
// reports `complete: false` plus a `remainingCount` when it ran out of time
// mid-batch. It also refuses official hikes outright. So a batch response is not
// simply "all or nothing" — the caller has to re-issue a batch until the server
// says it finished, and accumulate what actually happened.

import { i18n } from '../i18n'

const t = (key: string, named: Record<string, unknown> = {}): string => i18n.global.t(key, named)

export const BULK_DELETE_BATCH_SIZE = 10

// Hard stop on the resume loop so a pathological server response can't spin
// forever. 10 hikes per batch against a budget that fits several is generous.
export const MAX_BULK_DELETE_RESUMES = 20

export interface BulkDeleteBatchResult {
  destroyed?: boolean | null
  destroyedCount?: number | null
  destroyedIds?: string[] | null
  failedCount?: number | null
  skippedOfficialCount?: number | null
  remainingCount?: number | null
  complete?: boolean | null
  errors?: Array<{ fieldName?: string, errors?: string[] }> | null
}

export interface BulkDeleteProgress {
  destroyedIds: string[]
  skippedOfficialCount: number
  failedCount: number
}

export const emptyBulkDeleteProgress = (): BulkDeleteProgress => ({
  destroyedIds: [],
  skippedOfficialCount: 0,
  failedCount: 0
})

export function chunkIds (ids: string[], batchSize: number): string[][] {
  const size = Math.max(1, Math.floor(batchSize))
  const batches: string[][] = []
  for (let i = 0; i < ids.length; i += size) {
    batches.push(ids.slice(i, i + size))
  }
  return batches
}

// A batch is finished when the server says it completed the pass. `complete` is
// nullable on the wire, so treat a missing value as finished — a backend that
// doesn't report progress did all it was going to do in one call.
export function batchIsFinished (result: BulkDeleteBatchResult | null | undefined): boolean {
  if (!result) return true
  if (result.complete === false) return false
  return (result.remainingCount ?? 0) <= 0
}

// Only resume when the last pass actually removed something. An incomplete pass
// that deleted nothing means the leftovers are being refused, not starved of
// time, and re-issuing it would loop forever.
export function shouldResumeBatch (result: BulkDeleteBatchResult | null | undefined): boolean {
  if (batchIsFinished(result)) return false
  return (result?.destroyedCount ?? 0) > 0
}

// Fold one batch response into the running totals. Only ids the server actually
// removed count as deleted; when it doesn't report ids (older backend), fall
// back to the batch it was asked about, but only if it claims outright success.
export function accumulateBatch (
  progress: BulkDeleteProgress,
  batchIds: string[],
  result: BulkDeleteBatchResult | null | undefined
): BulkDeleteProgress {
  const destroyedIds = result?.destroyedIds
  const removed = Array.isArray(destroyedIds)
    ? destroyedIds
    : (result?.destroyed === true && (result?.destroyedCount ?? 0) > 0 ? batchIds : [])
  const alreadyRemoved = new Set(progress.destroyedIds)

  return {
    destroyedIds: [...progress.destroyedIds, ...removed.filter(id => !alreadyRemoved.has(id))],
    // A resumed batch re-reports the official hikes still in scope, so take the
    // largest value seen rather than summing over passes.
    skippedOfficialCount: Math.max(progress.skippedOfficialCount, result?.skippedOfficialCount ?? 0),
    failedCount: Math.max(progress.failedCount, result?.failedCount ?? 0)
  }
}

// Joins a payload's per-record error groups into one message: the messages within a
// record with ", " and the records themselves with "; ".
export function payloadErrorMessage (errors: BulkDeleteBatchResult['errors']): string {
  if (!Array.isArray(errors) || errors.length === 0) return ''
  return errors
    .map(err => Array.isArray(err?.errors) ? err.errors.join(', ') : null)
    .filter(Boolean)
    .join('; ')
}

// A function so the message is translated when shown, not at module load.
export const officialHikeDeleteMessage = (): string =>
  t('utils.bulk_hike_deletion.official_hikes_cannot_be_deleted')
// Developer-facing: names the mutation, so it stays untranslated.
export const NO_CHANGES_DELETE_MESSAGE = 'DestroyUserHikes mutation returned no changes'

// What a single-hike `destroyUserHikes` response means: null when the hike is gone,
// otherwise the reason it isn't.
//
// Success is `destroyedCount > 0` alone — not `destroyed && destroyedCount > 0`.
// `destroyed` means "the whole pass completed with no failures", which is false when
// the server also skipped a protected hike or ran out of time, yet this hike may still
// have been removed. The count is the fact about *this* hike.
export function singleDeleteFailure (result: BulkDeleteBatchResult | null | undefined): string | null {
  if ((result?.destroyedCount ?? 0) > 0) return null

  const payloadErrors = payloadErrorMessage(result?.errors)
  if (payloadErrors) return payloadErrors

  if ((result?.skippedOfficialCount ?? 0) > 0) return officialHikeDeleteMessage()

  return NO_CHANGES_DELETE_MESSAGE
}

const officialSkipMessage = (count: number): string =>
  count === 1
    ? t('utils.bulk_hike_deletion.official_skip_one', { count })
    : t('utils.bulk_hike_deletion.official_skip_many', { count })

// Anything the user selected that wasn't removed and isn't a protected official
// hike counts as a failure — that's what makes the message honest when the
// server timed out or refused a delete.
export function bulkDeleteFailureCount (progress: BulkDeleteProgress, requestedCount: number): number {
  return Math.max(0, requestedCount - progress.destroyedIds.length - progress.skippedOfficialCount)
}

export function bulkDeleteSummary (progress: BulkDeleteProgress, requestedCount: number): string {
  const deleted = progress.destroyedIds.length
  const failed = bulkDeleteFailureCount(progress, requestedCount)

  // Nothing deleted because everything selected was protected — say only that,
  // rather than reporting a failure the user can do nothing about.
  if (deleted === 0 && failed === 0 && progress.skippedOfficialCount > 0) {
    return officialSkipMessage(progress.skippedOfficialCount)
  }

  const parts: string[] = [
    deleted > 0
      ? (deleted === 1
          ? t('utils.bulk_hike_deletion.deleted_one', { count: deleted })
          : t('utils.bulk_hike_deletion.deleted_many', { count: deleted }))
      : (requestedCount === 1
          ? t('utils.bulk_hike_deletion.could_not_delete_selected')
          : t('utils.bulk_hike_deletion.could_not_delete_many', { count: requestedCount }))
  ]

  if (progress.skippedOfficialCount > 0) parts.push(officialSkipMessage(progress.skippedOfficialCount))
  if (deleted > 0 && failed > 0) {
    parts.push(failed === 1
      ? t('utils.bulk_hike_deletion.failed_one', { count: failed })
      : t('utils.bulk_hike_deletion.failed_many', { count: failed }))
  }

  return parts.join(' ')
}
